import classnames from 'classnames'
import CSSToolTip from 'client/components/common/CSSToolTip/CSSToolTip'
import Input from 'client/components/common/Input/Input'
import useOnClickOutside from 'client/hooks/useOnClickOutside'
import { DraftFieldsInterface } from 'client/Interfaces//Draft/FieldsInterface'
import { FieldInterface } from 'client/Interfaces/Draft/FieldInterface'
import { paymentReferenceTypes } from 'client/Interfaces/Draft/FieldsInterface'
import {
  correctReferenceNumber,
  correctStructuredCommunication,
  generateReferenceNumber,
  generateStructuredCommunication,
  getVisualStructuredCommunication,
} from 'client/Other/functions/referenceNumberFunctions'
import { setBaseClass } from 'client/utils'
import * as React from 'react'

import { l } from '../../../Other/Translation'
import { REFERENCE_NUMBER_TYPES_MAP } from '../ReferenceFieldsSettings'
import './ReferenceNumberField.scss'

interface ReferenceNumberFieldInterface {
  onFieldChange: (field: FieldInterface) => void
  borderless: boolean
  editable: boolean
  fields: DraftFieldsInterface
}

const isReferenceNumValid = (referenceNumber, type) => {
  switch (type) {
    case paymentReferenceTypes.SPY_BE:
      return /^[0-9]{12}$/.test(referenceNumber)
    case paymentReferenceTypes.SPY_FI:
      return !referenceNumber || /^.{4,20}$/.test(referenceNumber)
    case paymentReferenceTypes.ISO:
      return !referenceNumber || /^RF[0-9]{3,23}$/.test(referenceNumber)
  }
}

const getCorrectReferenceNumber = (refNumber, referenceNumberType) => {
  if (referenceNumberType === paymentReferenceTypes.SPY_FI) {
    return correctReferenceNumber(refNumber)
  } else if (referenceNumberType === paymentReferenceTypes.SPY_BE) {
    return correctStructuredCommunication(refNumber)
  } else {
    return refNumber || ''
  }
}

export const getReferenceNumberForType = (invoiceNumber, referenceNumberType) => {
  if (referenceNumberType === paymentReferenceTypes.SPY_FI) {
    return generateReferenceNumber(invoiceNumber)
  }
  if (referenceNumberType === paymentReferenceTypes.SPY_BE) {
    return generateStructuredCommunication()
  }
}

export const getDisplayRefNumber = (referenceNumber, referenceNumberType) => {
  if (
    referenceNumberType === paymentReferenceTypes.SPY_BE &&
    isReferenceNumValid(referenceNumber, referenceNumberType)
  ) {
    return getVisualStructuredCommunication(referenceNumber)
  } else if (referenceNumberType === paymentReferenceTypes.ISO) {
    return (referenceNumber || '').toUpperCase().replace(/^RF/, '')
  } else {
    return referenceNumber
  }
}

const cls = setBaseClass('ReferenceNumberField')

export const ReferenceNumberField = ({
  fields,
  editable,
  borderless,
  onFieldChange,
}: ReferenceNumberFieldInterface): React.ReactElement => {
  const paymentReferenceTypeField = fields.paymentReferenceType
  const referenceNumberType = paymentReferenceTypeField.value
  const referenceNumber = fields.referenceNumber.value
  const [referenceNumbers, setReferenceNumbers] = React.useState({ [referenceNumberType]: referenceNumber })

  React.useEffect(() => {
    if (!referenceNumbers[referenceNumberType]) {
      const newRefNumber = getReferenceNumberForType(fields.invoiceNumber?.value, referenceNumberType)
      setReferenceNumbers({
        ...referenceNumbers,
        [referenceNumberType]: newRefNumber,
      })
      onFieldChange({ ...fields.referenceNumber, value: newRefNumber })
    } else if (referenceNumbers[referenceNumberType] && referenceNumber !== referenceNumbers[referenceNumberType]) {
      onFieldChange({ ...fields.referenceNumber, value: referenceNumbers[referenceNumberType] })
    }
  }, [referenceNumberType])

  return (
    <div className={cls()}>
      <ReferenceNumberFieldLabel
        selected={referenceNumberType}
        disabled={!editable}
        onChange={(newType) => {
          if (Object.prototype.hasOwnProperty.call(referenceNumbers, newType)) {
            onFieldChange({ ...fields.referenceNumber, value: referenceNumbers[newType] })
          } else {
            const newReferenceNumber = [paymentReferenceTypes.SPY_BE, paymentReferenceTypes.SPY_FI].includes(newType)
              ? getReferenceNumberForType(fields.invoiceNumber.value, newType)
              : ''
            onFieldChange({
              ...fields.referenceNumber,
              value: newReferenceNumber,
            })
          }
          onFieldChange({ ...paymentReferenceTypeField, value: newType })
        }}
      />

      <div className={cls('__input')}>
        <Input
          {...{
            className: [classnames({ invalid: !isReferenceNumValid(referenceNumber, referenceNumberType) })],
            value: getDisplayRefNumber(referenceNumber, referenceNumberType),
            type: referenceNumberType === paymentReferenceTypes.SPY_FI ? 'number' : 'text',
            invalid: false,
            automation: '',
            ...(referenceNumberType === paymentReferenceTypes.ISO ? { startAdornment: 'RF' } : {}),
            placeHolder: l(`invoiceEditor.referenceNumber.${referenceNumberType}.placeholder`),
            onBlur: (value) => {
              if (referenceNumberType === paymentReferenceTypes.SPY_BE) {
                return
              }
              value = value.replace(/\s/g, '')
              if (referenceNumberType === paymentReferenceTypes.ISO && value.length >= 2) {
                value = value.toUpperCase().replace(/^[RF]*/, '')
                value = `RF${value}`
              }
              const referenceNumber = !value
                ? getReferenceNumberForType(fields.invoiceNumber.value, referenceNumberType)
                : value
              onFieldChange({ ...fields.referenceNumber, value: referenceNumber })
              setReferenceNumbers({ ...referenceNumbers, [referenceNumberType]: referenceNumber })
            },
            editable: editable && referenceNumberType !== paymentReferenceTypes.SPY_BE,
            borderless,
          }}
        />
        {!isReferenceNumValid(referenceNumber, referenceNumberType) && (
          <div className={cls('__input-invalid-msg')}>
            <div className={cls('__input-invalid-msg-inner')}>
              {l(`invoiceEditor.referenceNumber.${referenceNumberType}.invalid`)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ReferenceNumberFieldLabel = ({ selected, disabled, onChange }) => {
  const [open, setOpen] = React.useState(false)
  const dropDownRef = React.useRef()
  useOnClickOutside(dropDownRef, () => {
    setOpen(false)
  })
  const menu =
    open && !disabled ? (
      <div className={cls('__label-menu-container')}>
        {Object.keys(REFERENCE_NUMBER_TYPES_MAP).map((k, i) => {
          return (
            <div
              className={classnames(cls('__label-menu-item'), { selected: k === selected })}
              onClick={() => {
                onChange(k)
                setOpen(false)
              }}
            >
              {l(REFERENCE_NUMBER_TYPES_MAP[k])}
            </div>
          )
        })}
      </div>
    ) : null

  return (
    <div ref={dropDownRef} className={cls('__label')}>
      <div className={cls('__label-dropdown')} onClick={() => setOpen(!open)}>
        <div className={classnames(cls('__label-text'), { open })} title={l(REFERENCE_NUMBER_TYPES_MAP[selected])}>
          {l(REFERENCE_NUMBER_TYPES_MAP[selected])}
        </div>
        <CSSToolTip
          header={null}
          body={<div dangerouslySetInnerHTML={{ __html: l(`invoice.referenceNumber.${selected}.helperText`) }}></div>}
        />
      </div>
      {menu}
    </div>
  )
}
